import { ServerError } from '@apollo/client/errors';
import { client } from '@passwordless-id/webauthn';
import { useContext, useEffect } from 'react';

import { MessagingContext } from '@sorare/wallet-shared';
import type { SignInWithPasskey } from '@sorare/wallet-shared/src/contexts/messaging';

import type { AcceptTermsInfo } from '#src/contexts/connection/index.tsx';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useSignInMutation } from '#src/contexts/currentUser/useSignIn.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';

import { useGeneratePasskeyChallenge } from './useGeneratePasskeyChallenge.ts';
import { useOnSignedIn } from './useOnSignedIn.ts';

const RATE_LIMIT_STATUS_CODE = 429;

export const useSignInWithPasskey = () => {
  const { registerHandler } = useContext(MessagingContext)!;
  const track = useEvents();
  const { promptNewDeviceConfirmation, promptTerms } = useConnectionContext();
  const signIn = useSignInMutation();
  const generatePasskeyChallenge = useGeneratePasskeyChallenge();
  const onSignedIn = useOnSignedIn();

  useEffect(
    () =>
      registerHandler<SignInWithPasskey>('signInWithPasskey', async () => {
        try {
          const challenge = await generatePasskeyChallenge();
          const passkey = await client.authenticate({
            challenge,
          });

          const response = await signIn({
            passkeyCredential: passkey,
            passkeyChallenge: challenge,
          });
          if (!response) return { error: 'Unexpected' };

          const { currentUser, errors, tcuToken } = response;
          const firstErrorMessage = errors[0]?.message;
          if (firstErrorMessage) {
            if (firstErrorMessage === 'authenticate_from_new_device') {
              promptNewDeviceConfirmation();
              return {};
            }
            if (firstErrorMessage === 'must_accept_tcus') {
              await new Promise<AcceptTermsInfo>((resolve, reject) => {
                return promptTerms(
                  {
                    closable: false,
                    tcuToken,
                  },
                  { resolve, reject }
                );
              });
              return {};
            }
            if (firstErrorMessage === 'unconfirmed') {
              track('View Unconfirmed Email Error On SignUp');
            }
          }

          if (!currentUser) return { error: 'Unexpected' };

          return onSignedIn(currentUser);
        } catch (error) {
          if (error === 'has_accepted_tcus_after_2FA') return {};
          if (
            ServerError.is(error) &&
            error.statusCode === RATE_LIMIT_STATUS_CODE
          )
            return { error: 'rate-limit' };
          return {
            error: error instanceof Error ? error.message : String(error),
          };
        }
      }),
    [
      registerHandler,
      signIn,
      track,
      onSignedIn,
      promptNewDeviceConfirmation,
      promptTerms,
      generatePasskeyChallenge,
    ]
  );
};
